/**
 * Meal Plan Sync Middleware
 *
 * Pushes every meal plan mutation to Firestore, so the week the user is
 * looking at is the week every other device — and the Sunday email — sees.
 *
 * Uses Redux Toolkit's createListenerMiddleware for clean side-effect handling.
 */

import { createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit';
import {
  removeFromWeek,
  addToWeek,
  addFreeTextMeal,
  moveMeal,
  replaceMeal,
  updateMealNotes,
  updateMealServings,
  clearWeek,
  setWeeklyPlan,
  getWeekStartForDate,
  markMealAsCooked,
  setLeftoverPortions,
  unmarkMealAsCooked,
  rateMeal,
  setSlotEntry,
  clearSlot,
  swapSlots,
  replaceSlotRecipe,
  setMealApproval,
  setMealCook,
  proposeMeal,
  acceptMeal,
  restoreWeek,
} from './mealPlanSlice';
import { syncMealPlanWeekToCloud } from '../services/mealPlanSyncService';
import { isFirebaseEnabled } from '../services/firebaseConfig';
import type { RootState } from './store';

// Create the listener middleware
export const mealPlanSyncMiddleware = createListenerMiddleware();

// Debounce map to prevent excessive syncs.
// `allowEmpty` rides along with the timer so a flush honours it too.
const syncDebounceMap = new Map<string, { timer: ReturnType<typeof setTimeout>; allowEmpty: boolean }>();
const DEBOUNCE_MS = 1000; // Wait 1 second after last change before syncing

/**
 * Helper to sync a week to cloud with debouncing.
 * Reads householdId from state at flush time so a pairing in flight gets
 * picked up automatically.
 *
 * `allowEmpty` writes a week that ended up with no meals. It is off by default
 * — an empty local week is usually one that simply hasn't hydrated yet, and
 * pushing it would wipe the cloud copy. Moving the last dish of a week to
 * another week is the case where the emptiness is real and must be published,
 * or the dish comes back on the next reload.
 */
const debouncedSync = (
  userId: string,
  weekStartDate: string,
  getState: () => unknown,
  allowEmpty = false
) => {
  // Clear any existing debounce timer for this week
  const existing = syncDebounceMap.get(weekStartDate);
  if (existing) {
    clearTimeout(existing.timer);
    allowEmpty = allowEmpty || existing.allowEmpty;
  }

  // Set new debounce timer
  const timer = setTimeout(async () => {
    syncDebounceMap.delete(weekStartDate);

    const state = getState() as RootState;
    const weekMeals = state.mealPlan.weeks[weekStartDate] || [];
    const householdId = state.auth?.householdId ?? null;

    if (weekMeals.length === 0 && !allowEmpty) {
      // No meals to sync
      return;
    }

    try {
      await syncMealPlanWeekToCloud(userId, weekStartDate, weekMeals, householdId);
      console.log(`🔄 Auto-synced changes to week ${weekStartDate} (scope: ${householdId ? 'household' : 'user'})`);
    } catch (error) {
      console.error(`Failed to auto-sync week ${weekStartDate}:`, error);
      // Don't throw - just log the error, local state is still valid
    }
  }, DEBOUNCE_MS);

  syncDebounceMap.set(weekStartDate, { timer, allowEmpty });
};

/**
 * Every week bucket an action changed.
 *
 * Most actions touch one week. A move or a swap can touch two, now that the
 * move picker can browse to another week: the dish leaves one bucket and lands
 * in another, and syncing only the destination would leave the source week's
 * copy on Firestore to come back on the next reload.
 */
const getAffectedWeekStarts = (
  action: any,
  state: RootState,
  originalState: RootState
): string[] => {
  const weeks = new Set<string>();

  // A move reads its source from the state *before* the reducer ran — after it,
  // the meal is already filed under the destination week.
  if (action.type === moveMeal.type) {
    const { mealId, newScheduledFor } = action.payload ?? {};
    for (const [weekStart, meals] of Object.entries(originalState.mealPlan.weeks)) {
      if (meals.some((m: any) => m.id === mealId)) {
        weeks.add(weekStart);
        break;
      }
    }
    if (newScheduledFor) weeks.add(getWeekStartForDate(newScheduledFor));
    return [...weeks];
  }

  if (action.type === swapSlots.type) {
    const { a, b } = action.payload ?? {};
    if (a?.scheduledFor) weeks.add(getWeekStartForDate(a.scheduledFor));
    if (b?.scheduledFor) weeks.add(getWeekStartForDate(b.scheduledFor));
    return [...weeks];
  }

  // An undo names its week outright.
  if (action.type === restoreWeek.type) {
    const weekStart = action.payload?.weekStart;
    return weekStart ? [weekStart] : [];
  }

  const single = getAffectedWeekStart(action, state);
  return single ? [single] : [];
};

/**
 * Get the week start date affected by an action
 */
const getAffectedWeekStart = (
  action: any,
  state: RootState
): string | null => {
  // For setWeeklyPlan: payload has { weekStartDate, meals }
  if (action.payload?.weekStartDate && action.payload?.meals) {
    return action.payload.weekStartDate;
  }

  // For actions with explicit weekStartDate as string (clearWeek)
  if (action.payload && typeof action.payload === 'string') {
    return action.payload || state.mealPlan.currentWeekStart;
  }

  // For actions with mealId, find which week contains the meal
  if (action.payload?.mealId) {
    for (const [weekStart, meals] of Object.entries(state.mealPlan.weeks)) {
      if (meals.some((m: any) => m.id === action.payload.mealId)) {
        return weekStart;
      }
    }
  }

  // For removeFromWeek (payload is just the mealId string)
  if (action.type === removeFromWeek.type && typeof action.payload === 'string') {
    for (const [weekStart, meals] of Object.entries(state.mealPlan.weeks)) {
      if (meals.some((m: any) => m.id === action.payload)) {
        return weekStart;
      }
    }
  }

  // For addToWeek, get week from scheduledFor
  if (action.payload?.scheduledFor) {
    return getWeekStartForDate(action.payload.scheduledFor);
  }

  // Destination of a move (moveMeal itself is handled above, with its source)
  if (action.payload?.newScheduledFor) {
    return getWeekStartForDate(action.payload.newScheduledFor);
  }

  // Slot-pair payloads (swapSlots is handled above, with both of its weeks)
  if (action.payload?.a?.scheduledFor) {
    return getWeekStartForDate(action.payload.a.scheduledFor);
  }

  return null;
};

// Listen for meal plan mutations that should trigger sync
mealPlanSyncMiddleware.startListening({
  matcher: isAnyOf(
    removeFromWeek,
    addToWeek,
    // Emptying a week is an edit like any other. Its only listener used to be
    // one that explicitly did nothing ("clearing a week removes confirmation,
    // so no sync needed"), so the cloud kept the old meals and handed them
    // back on the next reload (#610).
    clearWeek,
    addFreeTextMeal,
    moveMeal,
    replaceMeal,
    updateMealNotes,
    updateMealServings,
    setWeeklyPlan,
    markMealAsCooked,
    unmarkMealAsCooked,
    // Leftover portions declared after a cook (#663). Without this the count
    // stays on the device that recorded it and the other half of the household
    // never sees there is food to plan.
    setLeftoverPortions,
    rateMeal, // per-meal star rating — without this it never reaches Firestore
    // Non-recipe slot entries (note libre, dining, leftovers, skip) and slot
    // recipe ops — without these, entries never reach Firestore and get wiped
    // by the live-sync merge on the next reload.
    setSlotEntry,
    clearSlot,
    swapSlots,
    replaceSlotRecipe,
    setMealApproval, // household yes/no — without this it never reaches Firestore
    setMealCook, // who cooks a meal — without this it never reaches Firestore
    proposeMeal, // competing recipe proposal — without this it never reaches Firestore
    acceptMeal, // accept a proposal — without this it never reaches Firestore
    // « Annuler » after a move or a replace puts a week back from a snapshot.
    // Without this the undo stayed on the device and the next live snapshot
    // handed the replaced plan straight back (#845).
    restoreWeek,
  ),
  effect: async (action, listenerApi) => {
    // Check if Firebase is enabled
    if (!isFirebaseEnabled()) {
      return;
    }

    const state = listenerApi.getState() as RootState;
    const originalState = listenerApi.getOriginalState() as RootState;
    const userId = state.auth?.user?.uid;

    if (!userId) {
      return; // Not logged in
    }

    let affectedWeekStarts: string[] = [];

    // For removeFromWeek, use original state to find which week the meal was in
    if (action.type === removeFromWeek.type) {
      const mealId = action.payload as string;
      for (const [weekStart, meals] of Object.entries(originalState.mealPlan.weeks)) {
        if (meals.some((m: any) => m.id === mealId)) {
          affectedWeekStarts = [weekStart];
          break;
        }
      }
    } else {
      affectedWeekStarts = getAffectedWeekStarts(action, state, originalState);
    }

    // A move or a swap can leave its source week with nothing in it — that
    // emptiness is a real edit and has to reach Firestore, or the dish comes
    // back in its old week on the next reload. So can a removal: the meal was
    // in the week, so the week had loaded (#777 — the cook's × on a week's
    // only dinner, and a meal whose recipe was deleted, came back).
    //
    // An undo that empties a week is real only when the week held meals
    // before it: those were this session's edit. A snapshot of a week that was
    // empty because it had not loaded must never be published as empty.
    const restoresAnEdit =
      action.type === restoreWeek.type &&
      (originalState.mealPlan.weeks[(action.payload as { weekStart: string }).weekStart]?.length ?? 0) > 0;
    const emptiesAreReal =
      action.type === removeFromWeek.type ||
      action.type === moveMeal.type ||
      action.type === swapSlots.type ||
      action.type === clearWeek.type ||
      restoresAnEdit;

    // Sync every change so meal plans appear consistently across devices
    affectedWeekStarts.forEach((weekStart) => {
      debouncedSync(userId, weekStart, listenerApi.getState, emptiesAreReal);
    });
  },
});

/**
 * Flush all pending debounced writes to Firestore immediately.
 * Call this on visibilitychange → hidden so a tab close or device switch
 * doesn't silently drop a 1-second debounce window.
 */
export const flushPendingMealPlanSync = async (getState: () => unknown): Promise<void> => {
  const state = getState() as RootState;
  const userId = state.auth?.user?.uid;
  if (!userId || syncDebounceMap.size === 0) return;

  const pending = [...syncDebounceMap.entries()].map(([weekStartDate, entry]) => {
    clearTimeout(entry.timer);
    syncDebounceMap.delete(weekStartDate);
    return { weekStartDate, allowEmpty: entry.allowEmpty };
  });

  await Promise.allSettled(
    pending.map(({ weekStartDate, allowEmpty }) => {
      const s = getState() as RootState;
      const weekMeals = s.mealPlan.weeks[weekStartDate] || [];
      const householdId = s.auth?.householdId ?? null;
      if (weekMeals.length === 0 && !allowEmpty) return Promise.resolve();
      return syncMealPlanWeekToCloud(userId, weekStartDate, weekMeals, householdId);
    })
  );
};

export default mealPlanSyncMiddleware;
